import { useEffect, useState, useMemo } from 'react';
import { Box, Link, useTheme } from '@mui/material';
import { ReportHeaderComponent } from '../../../components/reports/ReportHeaderComponent/ReportHeaderComponent';
import { ReportTableComponent, ReportTableData } from '../../../components/reports/ReportTableComponent/ReportTableComponent';
import { GridColDef } from '@mui/x-data-grid';
import { SelectComponent, SelectOption } from '../../../components/core/SelectComponent/SelectComponent';
import { ExportMenuComponent } from '../../../components/reports/ExportMenuComponent/ExportMenuComponent';
import { getReportData } from '../../../services/reports/CurriculumReportService';
import { ExperienceColumnIcon } from './ExperienceColumnIcon';
import { studentProgressStyles } from './StudentProgress.styles';
import { toOptionalPositiveInteger } from '../../../services/Utilities';

// Types for report data
interface ReportRecord {
  student_id: number;
  class_id: number;
  class_name: string;
  first_name: string;
  last_name: string;
  experience_id: number;
  experience_name: string;
  ends_at: string;
  responses_per_student: number;
  responses_completed_percentage: number;
  quiz: number;
  percentage_completed: number;
  time_on_task: number;
  [key: string]: unknown;
}

interface ClassInfo {
  name: string;
  class_name: string;
  class_id: number;
}

interface ExperienceInfo {
  experience_id: number;
  experience_name: string;
  ends_at: string;
}

interface ClassFilter {
  class_id: number;
  class_name: string;
}

// Helper functions (ported from studentProgressReport.js)
function abbreviateNumber(num: number): string {
  num = Number(num) || 0;
  if (num < 1000) {
    return '' + num;
  }
  const power = Number(((num).toPrecision(2).split('e')[1] || [0, 0]).slice(1));
  const triplets = Math.floor(power / 3);
  let numericResult = (num / Math.pow(10, triplets * 3)).toFixed(1);
  if (numericResult.length > 4) {
    numericResult = (num / Math.pow(10, triplets * 3)).toFixed();
  }
  return numericResult + ['', 'K', 'M', 'B', 'T'][triplets];
}

function getFieldName(row: { experience_id: number }, baseField: string): string {
  return 'ex_' + row.experience_id + '_' + baseField;
}

function renderAvgResp(val: number | null | undefined): string {
  return val ? String(val) : '-';
}

function renderQuizScore(val: number | null | undefined): string {
  if (val || val === 0) {
    return val + '%';
  } else {
    return '-';
  }
}

function renderPercComplete(val: number): string {
  return val + '%';
}

function getTimeOnTask(timeInSeconds: number | null | undefined): string {
  const value = Math.round((timeInSeconds || 0) / 60);
  return value ? abbreviateNumber(value) : '-';
}

function getCustomElementBackgroundColor(columnName: string | null, value: number | string): string {
  if (!columnName) {
    return '';
  }
  const numValue = parseFloat(String(value));

  if (columnName.indexOf('responses_per_student') > -1) {
    if (!numValue) {
      return '#ffffff';
    }
    return 'rgba(253, 180, 147,' + numValue + ')';
  }

  if (columnName.indexOf('quiz') > -1) {
    if (isNaN(numValue)) {
      return '#ffffff';
    } else if (numValue > 49.5 && numValue < 50.5) {
      return '#ffffff';
    } else if (numValue < 50) {
      return 'rgba(230, 129, 114, ' + (1 - numValue * 0.02) + ')';
    } else if (numValue > 50) {
      return 'rgba(160, 223, 253, ' + (numValue * 0.02 - 1) + ')';
    }
  }

  if (columnName.indexOf('percentage_completed') > -1) {
    if (!numValue) {
      return '#ffffff';
    } else {
      return 'rgba(169, 169, 169, ' + numValue * 0.01 + ')';
    }
  }

  if (columnName.indexOf('time_on_task') > -1) {
    if (!numValue) {
      return '#ffffff';
    } else {
      let percentage = (numValue / (60 / 100));
      if (percentage > 99) {
        percentage = 100;
      }
      return 'rgba(245, 211, 40, ' + percentage * 0.01 + ')';
    }
  }

  return '';
}

function formatRecordsByStudents(classRecords: ReportRecord[]): ReportTableData[] {
  const byStdId: { [key: number]: ReportTableData } = {};

  classRecords.forEach((row) => {
    const id = row.student_id;
    if (!byStdId[id]) {
      byStdId[id] = {
        id: row.student_id,
        prettyName: row.first_name + ' ' + row.last_name,
        sortName: row.last_name + ' ' + row.first_name,
        student_id: row.student_id,
        class_id: row.class_id,
        class_name: row.class_name,
      };
    }

    byStdId[id][getFieldName(row, 'responses_per_student')] = renderAvgResp(row.responses_per_student);
    byStdId[id][getFieldName(row, 'responses_per_student_sort')] = parseFloat(String(row.responses_per_student)) || 0;
    byStdId[id][getFieldName(row, 'responses_per_student') + '_bg'] = getCustomElementBackgroundColor('responses_per_student', row.responses_completed_percentage);

    byStdId[id][getFieldName(row, 'quiz')] = renderQuizScore(row.quiz);
    byStdId[id][getFieldName(row, 'quiz_sort')] = parseFloat(String(row.quiz)) || 0;
    byStdId[id][getFieldName(row, 'quiz') + '_bg'] = getCustomElementBackgroundColor('quiz', row.quiz);

    byStdId[id][getFieldName(row, 'percentage_completed')] = renderPercComplete(row.percentage_completed);
    byStdId[id][getFieldName(row, 'percentage_completed_sort')] = parseFloat(String(row.percentage_completed)) || 0;
    byStdId[id][getFieldName(row, 'percentage_completed') + '_bg'] = getCustomElementBackgroundColor('percentage_completed', row.percentage_completed);

    byStdId[id][getFieldName(row, 'time_on_task')] = getTimeOnTask(row.time_on_task);
    byStdId[id][getFieldName(row, 'time_on_task_sort')] = row.time_on_task || 0;
    byStdId[id][getFieldName(row, 'time_on_task') + '_bg'] = getCustomElementBackgroundColor('time_on_task', parseFloat(getTimeOnTask(row.time_on_task)));
  });

  return Object.keys(byStdId).map((key) => byStdId[Number(key)]);
}

// Metric options for the report
const metricOptions: SelectOption[] = [
  { id: 'quiz', name: 'Quiz Score' },
  { id: 'responses_per_student', name: 'Total Responses' },
  { id: 'percentage_completed', name: 'Percent Complete' },
  { id: 'time_on_task', name: 'Time on Task (min.)' },
];

interface StudentProgressProps {
  onInited?: (value?: boolean) => void;
}

export const StudentProgress = ({ onInited }: StudentProgressProps = {}) => {
  const theme = useTheme();
  const [reportData, setReportData] = useState<ReportRecord[]>([]);
  const [classes, setClasses] = useState<ClassInfo[]>([]);
  const [experiencesList, setExperiencesList] = useState<ExperienceInfo[]>([]);
  const [filterClassName, setFilterClassName] = useState<string>('');
  const [filterClassId, setFilterClassId] = useState<number>(-1);
  const [filterMetric, setFilterMetric] = useState<string>('quiz');
  const [tableData, setTableData] = useState<ReportTableData[]>([]);
  const [classSelectionError, setClassSelectionError] = useState<string | null>(null);

  function updateReportView(data: ReportRecord[], filter?: ClassFilter): void {
    const uniqClasses: { [key: number]: ReportRecord } = {};
    const classesFromData = data
      .filter((rec) => {
        if (uniqClasses[rec.class_id] || !rec.class_id) {
          return false;
        }
        uniqClasses[rec.class_id] = rec;
        return true;
      })
      .map((rec) => ({
        name: rec.class_name,
        class_name: rec.class_name,
        class_id: rec.class_id,
      }));

    setClasses(classesFromData);

    if (!classesFromData.length) {
      return;
    }

    const initialClassId = toOptionalPositiveInteger(new URLSearchParams(window.location.search).get('class_id'));
    const selectedFilter = filter || (initialClassId ? classesFromData.find((clazz) => clazz.class_id === initialClassId) : classesFromData[0]);
    if (!selectedFilter) {
      setClassSelectionError('The requested class is not available for this report.');
      setFilterClassId(-1);
      setFilterClassName('');
      setExperiencesList([]);
      setTableData([]);
      return;
    }

    setClassSelectionError(null);
    setFilterClassName(selectedFilter.class_name);
    setFilterClassId(selectedFilter.class_id);

    const uniqueExpIds: { [key: number]: boolean } = {};
    const experiences = data
      .filter((rec) => rec.class_id === selectedFilter.class_id)
      .filter((rec) => {
        if (uniqueExpIds[rec.experience_id]) {
          return false;
        }
        uniqueExpIds[rec.experience_id] = true;
        return true;
      })
      .sort((a, b) => new Date(a.ends_at).getTime() - new Date(b.ends_at).getTime());

    setExperiencesList(experiences);

    const classRecords = data.filter((rec) => rec.class_id === selectedFilter.class_id);
    const formattedData = formatRecordsByStudents(classRecords);
    setTableData(formattedData);
  }

  useEffect(() => {
    getReportData('curriculum-student-progress-all')
      .then((data) => {
        console.log('Report data loaded:', data);
        const reportRecords = (data as { data?: ReportRecord[] })?.data || (data as ReportRecord[]);
        if (Array.isArray(reportRecords)) {
          setReportData(reportRecords);
          updateReportView(reportRecords);
        }
        onInited?.(true);
      })
      .catch((error) => {
        console.error('Failed to load report data:', error);
        onInited?.(true);
      });
  }, [onInited]);

  // Convert classes to SelectOption format
  const classOptions: SelectOption[] = useMemo(() => 
    classes.map((cls) => ({
      id: cls.class_id,
      name: cls.class_name,
    })),
    [classes]
  );

  // Callback for class selection
  const handleClassChange = (selectedId: string | number | (string | number)[]) => {
    const classId = typeof selectedId === 'number' ? selectedId : Number(selectedId);
    const selectedClass = classes.find((cls) => cls.class_id === classId);
    if (selectedClass) {
      updateReportView(reportData, {
        class_id: selectedClass.class_id,
        class_name: selectedClass.class_name,
      });
    }
  };

  // Callback for metric selection
  const handleMetricChange = (selectedId: string | number | (string | number)[]) => {
    const metricId = typeof selectedId === 'string' ? selectedId : String(selectedId);
    setFilterMetric(metricId);
  };

  // Callback for export - exports all students with selected metric to CSV
  const handleExport = () => {
    if (!reportData.length) {
      return;
    }

    // Get all unique experiences across all classes
    const uniqueExpIds: { [key: number]: boolean } = {};
    const allExperiences = reportData
      .filter((rec) => {
        if (uniqueExpIds[rec.experience_id]) {
          return false;
        }
        uniqueExpIds[rec.experience_id] = true;
        return true;
      })
      .sort((a, b) => new Date(a.ends_at).getTime() - new Date(b.ends_at).getTime());

    // Format all records by students (not filtered by class)
    const allStudentData = formatRecordsByStudents(reportData);

    // Build CSV headers: Class, Student, then experience names
    const headers = ['Class', 'Student', ...allExperiences.map((exp) => exp.experience_name)];

    // Build CSV rows
    const rows = allStudentData.map((student) => {
      const className = String(student.class_name || '');
      const studentName = String(student.prettyName || '');
      const experienceValues = allExperiences.map((exp) => {
        const fieldName = `ex_${exp.experience_id}_${filterMetric}`;
        const value = student[fieldName];
        return value !== undefined && value !== null ? String(value) : '';
      });
      return [className, studentName, ...experienceValues];
    });

    // Escape CSV values (handle commas and quotes)
    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map(escapeCSV).join(',')),
    ].join('\n');

    // Generate filename with metric name and current date
    const metricName = metricOptions.find((m) => m.id === filterMetric)?.name || filterMetric;
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    const filename = `Student Progress ${metricName} ${dateStr}.csv`;

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Callback for experience header click - navigates to experience dashboard
  const handleExperienceHeaderClick = (experienceId: number) => {
    window.location.pathname = `/experience/${experienceId}/dashboard/responses`;
  };

  // Callback for student name click
  const handleStudentClick = (studentId: number, classId: number) => {
    console.log('Student clicked:', studentId, 'Class:', classId);
    // TODO: Implement student click logic
  };

  // Generate table columns from experiences list
  const tableColumns = useMemo(() => {
    const studentColumn = {
      field: 'prettyName',
      headerName: 'Student',
      width: 200,
      filterable: true,
      renderHeader: () => (
        <Box sx={studentProgressStyles.studentColumnHeader(theme)}>Student</Box>
      ),
      renderCell: (params: { row: ReportTableData; value: string }) => (
        <Link
          component="button"
          variant="body1"
          onClick={() => handleStudentClick(params.row.student_id as number, params.row.class_id as number)}
          sx={studentProgressStyles.studentLink(theme)}
        >
          {params.value}
        </Link>
      ),
    };

    const experienceColumns = experiencesList.map((exp) => ({
      field: `ex_${exp.experience_id}_${filterMetric}`,
      headerName: exp.experience_name,
      description: exp.experience_name,
      width: 100,
      filterable: false,
      headerAlign: 'center' as const,
      align: 'center' as const,
      renderHeader: () => (
        <ExperienceColumnIcon 
          title={exp.experience_name} 
          size={24} 
          onClick={() => handleExperienceHeaderClick(exp.experience_id)}
        />
      ),
    }));

    return [studentColumn, ...experienceColumns] as GridColDef[];
  }, [experiencesList, filterMetric]);

  // Header components: [row][column]
  const headerComponents = [
    [
      <SelectComponent
        key="class-select"
        options={classOptions}
        value={filterClassId > 0 ? filterClassId : undefined}
        onChange={handleClassChange}
        label="Class"
      />,
      null,
      <SelectComponent
        key="metric-select"
        options={metricOptions}
        value={filterMetric}
        onChange={handleMetricChange}
        label="Metric"
      />,
      null,
      null,
      <Box key="export-menu-container" sx={studentProgressStyles.exportMenuContainer(theme)}>
        <ExportMenuComponent
          showPrint={false}
          showExport={true}
          onPrint={() => {}}
          onExport={handleExport}
        />
      </Box>,
    ],
  ];

  return (
    <Box sx={studentProgressStyles.container(theme)}>
      <Box sx={studentProgressStyles.headerContainer(theme)}>
        <ReportHeaderComponent
          rows={1}
          columns={6}
          components={headerComponents}
        />
      </Box>
      <Box sx={studentProgressStyles.tableContainer(theme)}>
        {classSelectionError && <Box role="alert">{classSelectionError}</Box>}
        <ReportTableComponent
          data={tableData}
          layout={{
            columns: tableColumns,
            pageSize: 10,
            enableFiltering: true,
          }}
        />
      </Box>
    </Box>
  );
};

export default StudentProgress;
